import SankeyContainer from "../../UI/Charts/SankeyChart/SankeyContainer";
import SunburstContainer from "../../UI/Charts/SunburstChart/SunburstContainer";
import BarContainer from "../../UI/Charts/BarChart/BarContainer";
import { BoxTitle } from "./Overview";
import Tooltip from "../../../Components/Tooltip";
import { useEffect, useRef, useState } from "react";
import { NoDataBox } from "../../UI/Charts/NoDataFoundMessage";
import html2canvas from "html2canvas";
import sfdImg from "../../../Assets/Images/SFD.PNG"
import useApi from "../../../Hooks/useApi";



const getDataSourceId = (city) => {
  return (
    city?.dataSource?.find((item) => item.group === "Surveys")?.surveyId || null
  );
};



const BarChart = (city) => {
  return (
    <div className="w-full">
      <BarContainer
        width={500}
        height={300}
        surveyId={getDataSourceId(city)}
        locationId={city?.city?.id}
        cityData={city}
      />
    </div>
  );
};

const ShankeyChart = (city, type) => {
  return (
    <div className="w-full">
      <SankeyContainer
        surveyId={getDataSourceId(city)}
        locationId={city?.city?.id}
        type={type}
        cityData={city}
      />
    </div>
  );
};

const SunburstChart = (city, type) => {
  return (
    <div className="w-full">
      <SunburstContainer
        width={600}
        height={400}
        surveyId={getDataSourceId(city)}
        locationId={city?.city?.id}
        type={type}
      />
    </div>
  );
};




const SingleSfdImage = ({ cityData, isComparison }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const printRef = useRef(null);
  const { request } = useApi();
  
  const cityName = cityData?.city?.name;
  const locationId = cityData?.city?.id;
  const year = cityData?.year;

  useEffect(() => {
    const fetchSfdImage = async () => {
      if (!locationId || !year) {
        setError(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);

 
        const response = await request(
          "GET",
          `/v1/shit-flow-diagram/locations/${locationId}/${year}`,
          {
            responseType: 'blob', 
          },
          "survey_service"
        );


        if (!response || response.size === 0) {
          console.warn(`No SFD image found for location ${locationId}, year ${year}`);
          setError(true);
          setLoading(false);
          return;
        }

       
        const blob = new Blob([response], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);
        setLoading(false);
      } catch (err) {

        if (err?.response?.status === 404) {
          console.warn(`SFD image not found for location ${locationId}, year ${year}`);
        } else {
          console.error('Failed to fetch SFD image:', err);
        }
        setError(true);
        setLoading(false);
      }
    };

    fetchSfdImage();

    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [locationId, year]);

  const handleDownload = async () => {
    if (!printRef.current) return;
    try {
      const canvas = await html2canvas(printRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: "#ffffff",
      });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `SFD_${cityName}_${year}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download image:", err);
      alert("Could not download image.");
    }
  };

  if (loading) {
    return (
      <div className="h-full w-full flex justify-center items-center">
        <div className="text-gray-500">Loading SFD image...</div>
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className="h-full w-full">
        <NoDataBox message={`No SFD diagram found`} />
      </div>
    );
  }

  return (
    <div className={`w-full flex flex-col justify-center items-center
        ${isComparison ? "h-full" : "h-auto items-center"} 
      `}
    >
      <button
        className={`
          bg-[#2f4b7c] text-white font-bold text-xs py-2 px-4 rounded 
          hover:bg-[#1d3557] transition-colors -z-10
          ${isComparison 
            ? "self-start mb-4"       
            : "self-start mb-4 lg:absolute top-0 left-0 " 
          }
        `}
        onClick={handleDownload}
        title="Download as PNG"
      >
        Export as PNG
      </button>

      <div id="sfd-img" className={`${isComparison ? "flex justify-center items-center w-full relative flex-grow" : "flex justify-center items-center w-full"}`}>
        <img
          ref={printRef}
          src={imageUrl}
          className={`
            object-contain rounded-lg 
            ${isComparison ? "w-full h-full max-h-full" : "max-w-full h-auto max-h-[60vh]"}
          `}
          onError={() => setError(true)}
          alt={`${cityName} SFD - ${year}`}
        />
      </div>
    </div>
  );
};

const SfdSection = ({ firstCity, secondCity, isComparison }) => {
  return (
    <div>
      <p className="chart-title-style">Shit Flow Diagram (SFD)</p>
      
      <div 
        className={`
          relative 
          ${isComparison 
            ? "flex gap-4 w-full flex-col lg:flex-row justify-center items-stretch" 
            : "w-full flex justify-center" 
          }
        `}
      >
        <div className={`flex flex-col justify-center items-center ${isComparison 
          ? "w-full lg:w-1/2" 
          : "w-full lg:w-3/5"
        }`}>
          <SingleSfdImage cityData={firstCity} isComparison={isComparison} />
        </div>
        
        {isComparison && secondCity && (
          <div className="w-full lg:w-1/2 flex flex-col">
            <SingleSfdImage cityData={secondCity} isComparison={isComparison} />
          </div>
        )}
      </div>
    </div>
  );
};


const Sanitation = ({ isComparison, firstCity, secondCity }) => {

  return (
    <div
      id="Sanitation"
      className="border border-[#2F4B7C] border-opacity-30  p-5"
    >
      <BoxTitle title="Sanitation" />

      <div className="space-y-12">

          <SfdSection 
            firstCity={firstCity} 
            secondCity={secondCity} 
            isComparison={isComparison} 
        />
       <div>
          <p className="chart-title-style">Faecal Sludge Life Cycle</p>
          <div className="flex gap-4 w-full flex-col lg:flex-row">
            {ShankeyChart(firstCity, "Feacal_Sludge")}
            {isComparison ? ShankeyChart(secondCity, "Feacal_Sludge") : <></>}
          </div>
        </div>

        <div>
          <p className="chart-title-style">Containment Outlet Behaviour</p>

          <div className="flex gap-4 w-full flex-col lg:flex-row">
            {BarChart(firstCity)}
            {isComparison ? BarChart(secondCity) : <></>}
          </div>
        </div>

        <div className={`${isComparison ? "" : "grid 2xl:grid-cols-2 gap-5"} space-y-12`}>
          <div>
            <p className="chart-title-style">
              <Tooltip id={"Threat to Groundwater Contamination"}>Threat to Groundwater Contamination</Tooltip>
            </p>
            <div className="flex gap-4 w-full flex-col 2xl:flex-row">
              {SunburstChart(firstCity, "TGC")}
              {isComparison ? SunburstChart(secondCity, "TGC") : <></>}
            </div>
          </div>

          <div>
            <p className="chart-title-style">
              <Tooltip id={"Containment Accessibility Scenario"}>Containment Accessibility Scenario</Tooltip>
            </p>
            <div className="flex gap-4 w-full flex-col 2xl:flex-row">
              {SunburstChart(firstCity, "CAS")}
              {isComparison ? SunburstChart(secondCity, "CAS") : <></>}
            </div>
          </div>
        </div>

        <div>
          <p className="chart-title-style">Solid Waste Life Cycle</p>
          <div className="flex gap-4 w-full flex-col lg:flex-row">
            {ShankeyChart(firstCity, "Solid_Waste")}
            {isComparison ? ShankeyChart(secondCity, "Solid_Waste") : <></>}
          </div>
        </div>

        <div>
          <p className="chart-title-style">Solid Waste Management</p>
          <div className="flex gap-4 w-full flex-col lg:flex-row">
            {SunburstChart(firstCity, "SWD")}
            {isComparison ? SunburstChart(secondCity, "SWD") : <></>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sanitation;
